import React, { useEffect, useState } from "react";
import { Loader2, Pencil, Plus, Trash2 } from "lucide-react";
import { api, unwrapArray } from "@/lib/api";
import { useToast } from "@/context/ToastContext";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { formatINR } from "@/lib/utils";

export interface AttributeDef { id: string; name: string; field_type: "TEXT" | "NUMBER" | "LIST"; options: string[]; unit_label?: string | null; category?: string | null; sort_order: number }
interface AttributeValue { attribute_id: string; name: string; field_type: string; options: string[]; unit_label?: string | null; value: string }
export interface VariantRow { id: string; code: string; label: string; price_delta: number | string; status: boolean }

const select = "h-8 w-full rounded-md border border-border bg-background px-2 text-xs";

/** A product's attribute values and variants, in the product's side panel (changed only by people who may edit products) */
export function ProductOptionsPanel({ productId, unit, canEdit }: { productId: string; unit: string; canEdit: boolean }) {
  const { success, error } = useToast();
  const [values, setValues] = useState<AttributeValue[] | null>(null);
  const [draft, setDraft] = useState<Record<string, string>>({});
  const [variants, setVariants] = useState<VariantRow[] | null>(null);
  const [busy, setBusy] = useState(false);
  const [variantForm, setVariantForm] = useState<{ id?: string; code: string; label: string; price_delta: string; status: boolean } | null>(null);

  const load = async () => {
    try {
      const [v, vs] = await Promise.all([api.get(`/api/ProductOption/Values/${productId}`), api.get(`/api/ProductOption/Variants/${productId}`)]);
      const list = unwrapArray<AttributeValue>(v);
      setValues(list);
      setDraft(Object.fromEntries(list.map((x) => [x.attribute_id, x.value || ""])));
      setVariants(unwrapArray<VariantRow>(vs));
    } catch {
      setValues([]);
      setVariants([]);
    }
  };
  useEffect(() => {
    setValues(null);
    setVariants(null);
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productId]);

  const dirty = values?.some((v) => (draft[v.attribute_id] || "") !== (v.value || "")) ?? false;

  const saveValues = async () => {
    setBusy(true);
    try {
      await api.put(`/api/ProductOption/Values/${productId}`, { values: draft });
      success("Details saved", "");
      await load();
    } catch (e: any) {
      error("Could not save", e?.message || "Try again");
    } finally {
      setBusy(false);
    }
  };

  const saveVariant = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!variantForm) return;
    setBusy(true);
    try {
      const body = { code: variantForm.code.trim(), label: variantForm.label.trim(), price_delta: Number(variantForm.price_delta) || 0, status: variantForm.status };
      if (variantForm.id) await api.put(`/api/ProductOption/Variant/${variantForm.id}`, body);
      else await api.post(`/api/ProductOption/Variant/${productId}`, body);
      success("Variant saved", body.label);
      setVariantForm(null);
      await load();
    } catch (err: any) {
      error("Could not save", err?.message || "Try again");
    } finally {
      setBusy(false);
    }
  };

  const removeVariant = async (v: VariantRow) => {
    if (!window.confirm(`Remove "${v.label}"? If a quote, order or deal uses it, it is switched off instead.`)) return;
    try {
      const r: any = await api.delete(`/api/ProductOption/Variant/${v.id}`);
      const d = r?.AddtionalData ?? r?.data ?? r;
      success(d?.switched_off ? "Switched off" : "Removed", v.label);
      await load();
    } catch (e: any) {
      error("Could not remove", e?.message || "Try again");
    }
  };

  if (values === null || variants === null) return <div className="flex justify-center py-3"><Loader2 className="size-4 animate-spin text-muted-foreground" /></div>;
  if (!canEdit && values.every((v) => !v.value) && variants.length === 0) return null;

  return (
    <div className="flex flex-col gap-3 border-t border-border pt-3">
      {(values.length > 0 && (canEdit || values.some((v) => v.value))) && (
        <section className="flex flex-col gap-2">
          <h3 className="text-[11.5px] font-semibold uppercase tracking-wide text-muted-foreground">Details</h3>
          {values.map((v) => (
            <div key={v.attribute_id} className="flex flex-col gap-1">
              <Label className="text-[11.5px] text-muted-foreground">{v.name}{v.unit_label ? ` (${v.unit_label})` : ""}</Label>
              {!canEdit ? (
                <span className="text-[13px] font-medium">{v.value || "-"}</span>
              ) : v.field_type === "LIST" ? (
                <select className={select} value={draft[v.attribute_id] || ""} onChange={(e) => setDraft({ ...draft, [v.attribute_id]: e.target.value })}>
                  <option value="">-</option>
                  {v.options.map((o) => <option key={o} value={o}>{o}</option>)}
                </select>
              ) : (
                <Input className="h-8 text-xs" type={v.field_type === "NUMBER" ? "number" : "text"} value={draft[v.attribute_id] || ""} onChange={(e) => setDraft({ ...draft, [v.attribute_id]: e.target.value })} />
              )}
            </div>
          ))}
          {canEdit && dirty && <Button size="sm" onClick={saveValues} disabled={busy}>{busy && <Loader2 className="size-3.5 animate-spin mr-1.5" />}Save details</Button>}
        </section>
      )}

      {(canEdit || variants.length > 0) && (
        <section className="flex flex-col gap-1.5">
          <div className="flex items-center justify-between">
            <h3 className="text-[11.5px] font-semibold uppercase tracking-wide text-muted-foreground">Variants</h3>
            {canEdit && <Button variant="ghost" size="sm" className="h-6 px-1.5 text-xs" onClick={() => setVariantForm({ code: "", label: "", price_delta: "0", status: true })}><Plus className="size-3 mr-1" />Add</Button>}
          </div>
          {variants.length === 0 && <p className="text-xs text-muted-foreground">None. Add one for each colour, size or version sold on its own code.</p>}
          {variants.map((v) => (
            <div key={v.id} className={`flex items-center justify-between gap-2 rounded-md border border-border/70 px-2 py-1.5 text-xs ${v.status ? "" : "opacity-60"}`}>
              <div className="min-w-0">
                <div className="truncate font-medium">{v.label} {!v.status && <Badge variant="outline" className="ml-1">Off</Badge>}</div>
                <div className="font-mono text-[10.5px] text-muted-foreground">{v.code} · {Number(v.price_delta) === 0 ? "same price" : `${Number(v.price_delta) > 0 ? "+" : "−"}${formatINR(Math.abs(Number(v.price_delta)))} / ${unit.toLowerCase()}`}</div>
              </div>
              {canEdit && (
                <span className="flex shrink-0">
                  <Button variant="ghost" size="icon-sm" aria-label={`Edit ${v.label}`} onClick={() => setVariantForm({ id: v.id, code: v.code, label: v.label, price_delta: String(Number(v.price_delta)), status: v.status })}><Pencil className="size-3" /></Button>
                  <Button variant="ghost" size="icon-sm" aria-label={`Remove ${v.label}`} onClick={() => removeVariant(v)}><Trash2 className="size-3 text-danger" /></Button>
                </span>
              )}
            </div>
          ))}
        </section>
      )}

      <Dialog open={!!variantForm} onOpenChange={(o) => !o && setVariantForm(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{variantForm?.id ? "Edit variant" : "Add variant"}</DialogTitle>
            <DialogDescription>It sells at the product's price (or the customer's listed price) plus the difference.</DialogDescription>
          </DialogHeader>
          {variantForm && (
            <form onSubmit={saveVariant} className="space-y-3 py-1">
              <div className="space-y-1.5"><Label>Label *</Label><Input value={variantForm.label} onChange={(e) => setVariantForm({ ...variantForm, label: e.target.value })} maxLength={80} placeholder="e.g. Red / L" required /></div>
              <div className="space-y-1.5"><Label>Code *</Label><Input value={variantForm.code} onChange={(e) => setVariantForm({ ...variantForm, code: e.target.value })} maxLength={60} placeholder="e.g. TS-RED-L" required /></div>
              <div className="space-y-1.5"><Label>Price difference (₹, before GST)</Label><Input type="number" step="0.01" value={variantForm.price_delta} onChange={(e) => setVariantForm({ ...variantForm, price_delta: e.target.value })} /><p className="text-[11px] text-muted-foreground">Negative = cheaper than the product</p></div>
              {variantForm.id && <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={variantForm.status} onChange={(e) => setVariantForm({ ...variantForm, status: e.target.checked })} /> On sale</label>}
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setVariantForm(null)}>Cancel</Button>
                <Button type="submit" disabled={busy}>{busy && <Loader2 className="size-4 animate-spin mr-2" />}Save</Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

/** Where attributes are defined: name, kind, choices, and which products' category they belong to */
export function AttributesDialog({ open, onClose, categories }: { open: boolean; onClose: () => void; categories: string[] }) {
  const { success, error } = useToast();
  const [defs, setDefs] = useState<AttributeDef[] | null>(null);
  const [form, setForm] = useState<{ id?: string; name: string; field_type: string; options: string; unit_label: string; category: string } | null>(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      setDefs(unwrapArray<AttributeDef>(await api.get("/api/ProductOption/Attributes")));
    } catch {
      setDefs([]);
    }
  };
  useEffect(() => {
    if (open) load();
  }, [open]);

  const save = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form) return;
    setBusy(true);
    try {
      const body = { name: form.name.trim(), field_type: form.field_type, options: form.field_type === "LIST" ? form.options.split(",").map((o) => o.trim()).filter(Boolean) : [], unit_label: form.unit_label.trim() || undefined, category: form.category || undefined };
      if (form.id) await api.put(`/api/ProductOption/Attribute/${form.id}`, body);
      else await api.post("/api/ProductOption/Attribute", body);
      success("Attribute saved", body.name);
      setForm(null);
      await load();
    } catch (err: any) {
      error("Could not save", err?.message || "Try again");
    } finally {
      setBusy(false);
    }
  };

  const remove = async (a: AttributeDef) => {
    if (!window.confirm(`Remove "${a.name}"? Its value is taken off every product (quotes and orders already made keep what they printed).`)) return;
    try {
      await api.delete(`/api/ProductOption/Attribute/${a.id}`);
      success("Removed", a.name);
      await load();
    } catch (e: any) {
      error("Could not remove", e?.message || "Try again");
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-xl">
        <DialogHeader>
          <DialogTitle>Product attributes</DialogTitle>
          <DialogDescription>Things worth saying about a product (size, colour, grade, warranty). Define them once, then fill them in on each product; they print on quotes and orders.</DialogDescription>
        </DialogHeader>
        {!form ? (
          <div className="space-y-2 py-1">
            {defs === null ? <Loader2 className="mx-auto size-4 animate-spin" /> : defs.length === 0 ? <p className="py-4 text-center text-sm text-muted-foreground">No attributes yet.</p> : defs.map((a) => (
              <div key={a.id} className="flex items-center justify-between gap-2 rounded-md border border-border px-3 py-2 text-sm">
                <div>
                  <div className="font-medium">{a.name} <span className="text-xs font-normal text-muted-foreground">{a.field_type === "LIST" ? `choose: ${a.options.join(", ")}` : a.field_type === "NUMBER" ? `number${a.unit_label ? ` (${a.unit_label})` : ""}` : "text"}</span></div>
                  <div className="text-xs text-muted-foreground">{a.category ? `Only ${a.category} products` : "Every product"}</div>
                </div>
                <span className="flex">
                  <Button variant="ghost" size="icon-sm" aria-label={`Edit ${a.name}`} onClick={() => setForm({ id: a.id, name: a.name, field_type: a.field_type, options: a.options.join(", "), unit_label: a.unit_label || "", category: a.category || "" })}><Pencil className="size-3.5" /></Button>
                  <Button variant="ghost" size="icon-sm" aria-label={`Remove ${a.name}`} onClick={() => remove(a)}><Trash2 className="size-3.5 text-danger" /></Button>
                </span>
              </div>
            ))}
            <Button size="sm" variant="outline" onClick={() => setForm({ name: "", field_type: "TEXT", options: "", unit_label: "", category: "" })}><Plus className="size-4 mr-1.5" />Add attribute</Button>
          </div>
        ) : (
          <form onSubmit={save} className="space-y-3 py-1">
            <div className="space-y-1.5"><Label>Name *</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} maxLength={60} placeholder="e.g. Colour, GSM, Warranty" required /></div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Kind</Label>
                <select className="h-9 w-full rounded-md border border-border bg-background px-2.5 text-sm" value={form.field_type} onChange={(e) => setForm({ ...form, field_type: e.target.value })}>
                  <option value="TEXT">Text</option>
                  <option value="NUMBER">Number</option>
                  <option value="LIST">Choose from a list</option>
                </select>
              </div>
              <div className="space-y-1.5">
                <Label>Belongs to</Label>
                <select className="h-9 w-full rounded-md border border-border bg-background px-2.5 text-sm" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}>
                  <option value="">Every product</option>
                  {categories.map((c) => <option key={c} value={c}>Only {c}</option>)}
                </select>
              </div>
            </div>
            {form.field_type === "LIST" && <div className="space-y-1.5"><Label>Choices (comma separated) *</Label><Input value={form.options} onChange={(e) => setForm({ ...form, options: e.target.value })} placeholder="Cotton, Linen, Silk" /></div>}
            {form.field_type === "NUMBER" && <div className="space-y-1.5"><Label>Unit shown after the number</Label><Input value={form.unit_label} onChange={(e) => setForm({ ...form, unit_label: e.target.value })} maxLength={20} placeholder="e.g. GSM, mm, years" /></div>}
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setForm(null)}>Back</Button>
              <Button type="submit" disabled={busy}>{busy && <Loader2 className="size-4 animate-spin mr-2" />}Save</Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
